import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { ClipboardCheck } from "lucide-react";

const md = (d) => {
  if (!d) return "-";
  const dt = new Date(`${d}T00:00:00`);
  return `${dt.getMonth() + 1}/${dt.getDate()}(${"日月火水木金土"[dt.getDay()]})`;
};

const fmtQty = (q) => (Number.isInteger(q) ? q : parseFloat(q.toFixed(2)));

// 発注保存前のプレビュー（配送日ごとに明細を表示し、確定で送信）
export const OrderPreviewDialog = ({ preview, onCancel, onConfirm, saving }) => {
  const lines = preview?.lines || [];
  const byDate = {};
  lines.forEach((l) => {
    (byDate[l.delivery_date] = byDate[l.delivery_date] || []).push(l);
  });
  const dates = Object.keys(byDate).sort();
  const changedCount = lines.filter((l) => l.changed && !l.removed).length;
  const removedLines = lines.filter((l) => l.removed).length;

  return (
    <Dialog open={!!preview} onOpenChange={(o) => !o && !saving && onCancel()}>
      <DialogContent className="max-w-xl w-[calc(100%-1.5rem)] rounded-lg p-0 gap-0" data-testid="order-preview-dialog">
        <DialogHeader className="px-5 pt-5 pb-3 border-b">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <ClipboardCheck className="h-5 w-5 text-emerald-600" />
            発注内容の確認
          </DialogTitle>
          <DialogDescription>
            内容をご確認のうえ「この内容で発注する」を押してください。<b className="text-slate-800">特に単位（kg・本など）にお間違いがないかご確認ください。</b>
            {changedCount > 0 && <span className="text-emerald-700 font-medium">（新規・変更 {changedCount}件）</span>}
            {removedLines > 0 && <span className="text-red-700 font-medium">（取消 {removedLines}件）</span>}
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[55vh] overflow-y-auto px-5 py-3 space-y-4" data-testid="order-preview-list">
          {dates.map((d) => (
            <div key={d}>
              <div className="text-sm font-bold text-slate-700 mb-1.5" data-testid={`order-preview-date-${d}`}>
                配送日 {md(d)}
                <span className="ml-2 text-xs font-normal text-slate-500">{byDate[d].length}件</span>
              </div>
              <ul className="divide-y border rounded-md">
                {byDate[d].map((l, i) => (
                  <li key={i} className={`flex items-center gap-2 px-3 py-2 text-sm ${l.removed ? "bg-red-50" : l.changed ? "bg-emerald-50" : ""}`} data-testid="order-preview-line">
                    <span className={`flex-1 min-w-0 truncate ${l.removed ? "text-slate-400 line-through" : "text-slate-800"}`}>
                      {l.name}
                      {l.usage_date !== d && <span className="ml-1.5 text-xs text-slate-500">（使用日 {md(l.usage_date)}）</span>}
                    </span>
                    {l.removed ? (
                      <span data-testid="order-preview-cancel-badge" className="shrink-0 text-[10px] px-1.5 rounded-sm bg-red-100 text-red-700 border border-red-200">取消</span>
                    ) : l.changed && <span className="shrink-0 text-[10px] px-1.5 rounded-sm bg-emerald-100 text-emerald-700 border border-emerald-200">{l.isNew ? "新規" : "変更"}</span>}
                    <span className={`shrink-0 flex items-baseline gap-1.5 ${l.removed ? "opacity-40 line-through" : ""}`}>
                      <span className="text-lg font-bold tabular-nums text-slate-900">{fmtQty(l.quantity)}</span>
                      <span
                        data-testid="order-preview-unit"
                        className={`min-w-[2.5rem] text-center px-2 py-0.5 rounded-md text-base font-bold border ${/kg|ｋｇ|キロ/i.test(l.unit || "") ? "bg-amber-100 text-amber-800 border-amber-300" : "bg-sky-100 text-sky-800 border-sky-300"}`}
                      >
                        {l.unit || "-"}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
          {preview?.removedCount > 0 && (
            <div className="text-sm text-red-700" data-testid="order-preview-removed">分けて発注した明細の削除：{preview.removedCount}件</div>
          )}
        </div>
        <DialogFooter className="px-5 py-3 border-t gap-2 sm:gap-2">
          <Button variant="outline" onClick={onCancel} disabled={saving} data-testid="order-preview-back">
            戻って修正する
          </Button>
          <Button onClick={onConfirm} disabled={saving} data-testid="order-preview-confirm" className="bg-emerald-600 hover:bg-emerald-700 text-white">
            {saving ? "送信中..." : "この内容で発注する"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
